import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Avatar, Loading, Modal, inputCls, labelCls, btnPrimary } from "@/components/kit";
import { Barbell, Plus, Users, Medal, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Teams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "", season: "2025-26" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAD = user?.role === "athletic_director";

  const load = async () => { const r = await api.get("/teams"); setTeams(r.data); };
  useEffect(() => { load(); }, []);

  const viewTeam = async (id) => { const r = await api.get(`/teams/${id}`); setDetail(r.data); };

  const removeTeam = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete "${detail.name}"? This removes the team and its scheduled events. Players, coaches and grades are kept.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/teams/${detail.id}`);
      toast.success("Team deleted");
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete team"); }
    finally { setDeleting(false); }
  };

  const create = async () => {
    setSaving(true);
    try { await api.post("/teams", { name: form.name, sport: form.sport, season: form.season, coach_ids: [], athlete_ids: [] }); toast.success("Team created"); setOpen(false); setForm({ name: "", sport: "", season: "2025-26" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); } finally { setSaving(false); }
  };

  if (!teams) return <Loading />;

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-6xl mx-auto">
      <PageHeader eyebrow={isAD ? "Manage" : "My"} title="Teams" accent="" icon={Barbell}>
        {isAD && <button onClick={() => setOpen(true)} className={btnPrimary} data-testid="add-team-button"><Plus size={18} weight="bold" /> New Team</button>}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((t, i) => (
          <Card key={t.id} delay={i * 0.05} className="p-6 hover:scale-[1.02] hover:border-[#FF3B30]/40 transition-all cursor-pointer" onClick={() => viewTeam(t.id)} data-testid={`team-card-${t.id}`}>
            <div className="flex items-center justify-between mb-4">
              <Barbell size={28} weight="fill" color="#FF3B30" />
              <span className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">{t.season}</span>
            </div>
            <h3 className="font-head text-2xl font-bold uppercase tracking-tight">{t.name}</h3>
            <div className="text-sm text-[#737373] mb-4">{t.sport}</div>
            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]"><Users size={16} weight="bold" /> {t.athlete_count} athletes</div>
          </Card>
        ))}
        {teams.length === 0 && <div className="text-[#737373]">No teams yet.</div>}
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name} testid="team-detail-modal">
        {detail && (
          <div className="space-y-5">
            <div className="text-sm text-[#A3A3A3]">{detail.sport} · {detail.season}</div>
            <div>
              <div className={labelCls}><Medal size={14} className="inline mr-1" /> Coaches</div>
              <div className="space-y-2">
                {detail.coaches.map((c) => <div key={c.id} className="flex items-center gap-2"><Avatar name={c.name} size={32} color="#FF9F0A" /><span className="text-white text-sm">{c.name}</span></div>)}
                {detail.coaches.length === 0 && <div className="text-sm text-[#737373]">No coach assigned</div>}
              </div>
            </div>
            <div>
              <div className={labelCls}>Roster ({detail.athletes.length})</div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {detail.athletes.map((a) => <div key={a.id} className="flex items-center gap-2"><Avatar name={a.name} size={32} color="#34C759" /><span className="text-white text-sm">{a.name}</span></div>)}
                {detail.athletes.length === 0 && <div className="text-sm text-[#737373]">No athletes yet</div>}
              </div>
            </div>
            {isAD && (
              <div className="pt-4 border-t border-white/10">
                <button onClick={removeTeam} disabled={deleting} data-testid={`delete-team-${detail.id}`}
                  className="w-full flex items-center justify-center gap-2 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white disabled:opacity-60 font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-sm transition-colors">
                  <Trash size={18} weight="bold" /> {deleting ? "Deleting…" : "Delete Team"}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Team" testid="team-modal">
        <div className="space-y-4">
          <div><label className={labelCls}>Team Name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Varsity Basketball" data-testid="team-name-input" /></div>
          <div><label className={labelCls}>Sport</label><input className={inputCls} value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} placeholder="Basketball" data-testid="team-sport-input" /></div>
          <div><label className={labelCls}>Season</label><input className={inputCls} value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} data-testid="team-season-input" /></div>
          <button onClick={create} disabled={saving || !form.name} className={`${btnPrimary} w-full`} data-testid="save-team-button">{saving ? "Creating…" : "Create Team"}</button>
        </div>
      </Modal>
    </div>
  );
}
